import React, { useEffect, useRef, useState } from 'react';
import {
  Box,
  Button,
  ButtonBase,
  Switch,
  TextField,
  Typography
} from '@material-ui/core';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import {
  CheckCircleOutlineRounded as CheckCircleIcon,
  FileCopyRounded as CopyIcon,
  LinkRounded as LinkIcon,
  LockRounded as LockIcon,
  PeopleOutlineRounded as PeopleIcon
} from '@material-ui/icons';

import { baseUrl, useDebounce } from 'framework';
import { Line } from 'components';
import { Org } from 'domain-types';

const LoginConfigurationPanel = styled(Box)`
  margin: ${p => p.theme.spacing(2, 0, 3)};
  padding: ${p => p.theme.spacing(3)}px;
  border: 1px solid rgba(11, 68, 112, 0.14);
  border-radius: 18px;
  background: linear-gradient(
    180deg,
    rgba(11, 68, 112, 0.04) 0%,
    rgba(255, 255, 255, 0.98) 100%
  );

  ${p => p.theme.breakpoints.down('sm')} {
    padding: ${p => p.theme.spacing(2)}px;
  }
`;

const LoginProviderOptions = styled(Box)`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
  gap: ${p => p.theme.spacing(2)}px;
`;

const LoginProviderOption = styled(ButtonBase)<{ $selected: boolean }>`
  width: 100%;
  display: flex;
  align-items: stretch;
  text-align: left;
  border: 1px solid
    ${p => (p.$selected ? 'rgba(11, 68, 112, 0.3)' : 'rgba(11, 68, 112, 0.12)')};
  border-radius: 14px;
  background: ${p =>
    p.$selected ? 'rgba(11, 68, 112, 0.08)' : 'rgba(255, 255, 255, 0.9)'};
`;

const LoginProviderOptionBody = styled(Box)`
  width: 100%;
  padding: ${p => p.theme.spacing(2)}px;
`;

const LoginProviderOptionHeader = styled(Box)`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${p => p.theme.spacing(1)}px;
  margin-bottom: ${p => p.theme.spacing(1)}px;
`;

const LoginProviderBadge = styled(Typography)`
  padding: ${p => p.theme.spacing(0.5, 1)};
  border-radius: 999px;
  background: rgba(11, 68, 112, 0.12);
  color: rgba(11, 68, 112, 0.95);
  font-weight: 600;
`;

const LoginProviderDetails = styled(Box)`
  margin-top: ${p => p.theme.spacing(2)}px;
  padding-top: ${p => p.theme.spacing(2)}px;
  border-top: 1px solid rgba(11, 68, 112, 0.12);
`;

const LoginSectionHeading = styled(Typography)`
  margin-bottom: ${p => p.theme.spacing(0.75)}px;
`;

const LoginSectionCard = styled(Box)`
  margin-top: ${p => p.theme.spacing(2)}px;
  padding: ${p => p.theme.spacing(2.5)}px;
  border: 1px solid rgba(11, 68, 112, 0.1);
  border-radius: 16px;
  background: rgba(255, 255, 255, 0.82);

  ${p => p.theme.breakpoints.down('sm')} {
    padding: ${p => p.theme.spacing(2)}px;
  }
`;

const LoginStatusGrid = styled(Box)`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: ${p => p.theme.spacing(1.5)}px;
  margin: ${p => p.theme.spacing(2, 0)};
`;

const LoginStatusCard = styled(Box)`
  padding: ${p => p.theme.spacing(1.75)}px;
  border: 1px solid rgba(11, 68, 112, 0.1);
  border-radius: 14px;
  background: rgba(255, 255, 255, 0.92);
`;

const LoginStatusHeader = styled(Box)`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${p => p.theme.spacing(1)}px;
  margin-bottom: ${p => p.theme.spacing(1)}px;
`;

const LoginStatusIcon = styled(Box)`
  width: 36px;
  height: 36px;
  border-radius: 12px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: rgba(11, 68, 112, 0.08);
  color: rgba(11, 68, 112, 0.86);
`;

const LoginStatusBadge = styled(Typography)<{
  $tone: 'positive' | 'warning' | 'neutral';
}>`
  padding: ${p => p.theme.spacing(0.5, 1)};
  border-radius: 999px;
  font-weight: 600;
  background: ${p => {
    if (p.$tone === 'positive') {
      return 'rgba(46, 125, 50, 0.12)';
    }
    if (p.$tone === 'warning') {
      return 'rgba(237, 108, 2, 0.12)';
    }
    return 'rgba(11, 68, 112, 0.12)';
  }};
  color: ${p => {
    if (p.$tone === 'positive') {
      return 'rgba(27, 94, 32, 1)';
    }
    if (p.$tone === 'warning') {
      return 'rgba(140, 64, 0, 1)';
    }
    return 'rgba(11, 68, 112, 0.95)';
  }};
`;

const LoginStepGrid = styled(Box)`
  display: grid;
  gap: ${p => p.theme.spacing(1.25)}px;
  margin-top: ${p => p.theme.spacing(2)}px;
`;

const LoginStepCard = styled(Box)`
  display: grid;
  grid-template-columns: auto 1fr;
  gap: ${p => p.theme.spacing(1.5)}px;
  align-items: start;
  padding: ${p => p.theme.spacing(1.5)}px;
  border-radius: 14px;
  background: rgba(255, 255, 255, 0.88);
  border: 1px solid rgba(11, 68, 112, 0.08);
`;

const LoginStepNumber = styled(Box)`
  width: 32px;
  height: 32px;
  border-radius: 50%;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: rgba(11, 68, 112, 0.12);
  color: rgba(11, 68, 112, 0.95);
  font-weight: 700;
`;

const LoginSettingsGrid = styled(Box)`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: ${p => p.theme.spacing(2)}px;
  margin-top: ${p => p.theme.spacing(2)}px;
`;

const LoginField = styled(Box)`
  display: flex;
  flex-direction: column;
  gap: ${p => p.theme.spacing(0.75)}px;
`;

const LoginFieldHeader = styled(Box)`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${p => p.theme.spacing(1)}px;

  ${p => p.theme.breakpoints.down('sm')} {
    align-items: flex-start;
    flex-direction: column;
  }
`;

const LoginFieldLabel = styled(Typography)`
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: rgba(0, 0, 0, 0.56);
`;

const LoginFieldHint = styled(Typography)`
  color: rgba(0, 0, 0, 0.64);
`;

const LoginTextField = styled(TextField)`
  .MuiOutlinedInput-root {
    background: rgba(255, 255, 255, 0.94);
  }

  .MuiOutlinedInput-input.Mui-disabled {
    color: rgba(0, 0, 0, 0.72);
  }
`;

const CopyValueButton = styled(Button)`
  white-space: nowrap;
`;

const LoginToggleRow = styled(Box)`
  margin-top: ${p => p.theme.spacing(2.5)}px;
  padding: ${p => p.theme.spacing(1.5, 2)};
  border-radius: 14px;
  background: rgba(255, 255, 255, 0.86);
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${p => p.theme.spacing(2)}px;

  ${p => p.theme.breakpoints.down('sm')} {
    align-items: flex-start;
    flex-direction: column;
  }
`;

const LoginGuideValue = styled(Box)`
  margin-top: ${p => p.theme.spacing(2)}px;
`;

interface LoginTextInputProps {
  initialValue?: string;
  onChange?: (newValue: string) => unknown;
  type?: string;
  placeholder?: string;
  readOnly?: boolean;
}

function LoginTextInput({
  initialValue,
  onChange,
  type,
  placeholder,
  readOnly
}: LoginTextInputProps) {
  const [value, setValue] = useState<string | undefined>();
  const debouncedConfirm = useDebounce(onChange || (() => {}), 750);

  useEffect(() => {
    if (value === undefined || readOnly || !onChange) {
      return;
    }

    debouncedConfirm(value);
  }, [debouncedConfirm, onChange, readOnly, value]);

  return (
    <LoginTextField
      fullWidth
      variant="outlined"
      size="small"
      type={type || 'text'}
      value={value ?? initialValue ?? ''}
      placeholder={placeholder}
      InputProps={{ readOnly }}
      onChange={event =>
        event.target.value !== value && setValue(event.target.value)
      }
    />
  );
}

type LoginProvider = NonNullable<Org['authProvider']>;
type GoogleOidcUpdate = Partial<NonNullable<Org['googleOidc']>>;
type MicrosoftOidcUpdate = Partial<NonNullable<Org['microsoftOidc']>>;

interface Props {
  organization: string;
  org?: Org;
  onSelectAuthProvider: (provider: LoginProvider) => Promise<void> | void;
  onUpdateGoogleOidc: (fields: GoogleOidcUpdate) => Promise<void> | void;
  onUpdateMicrosoftOidc: (fields: MicrosoftOidcUpdate) => Promise<void> | void;
}

export function OrgAdminAuthenticationTab({
  organization,
  org,
  onSelectAuthProvider,
  onUpdateGoogleOidc,
  onUpdateMicrosoftOidc
}: Props) {
  const copyResetTimeout = useRef<number | null>(null);
  const [copiedField, setCopiedField] = useState<string | null>(null);
  const { t } = useTranslation();

  useEffect(
    () => () => {
      if (copyResetTimeout.current) {
        window.clearTimeout(copyResetTimeout.current);
      }
    },
    []
  );

  const activeAuthProvider = org?.authProvider || 'cognito';
  const isFlexibleSignin = activeAuthProvider === 'cognito';
  const googleAllowedDomainsValue = (
    org?.googleOidc?.allowedDomains || []
  ).join(', ');
  const hasGoogleClientId = !!org?.googleOidc?.clientId?.trim();
  const hasGoogleClientSecret = !!org?.googleOidc?.clientSecretConfigured;
  const hasRestrictedGoogleDomains =
    (org?.googleOidc?.allowedDomains || []).length > 0;
  const apiBaseUrl = baseUrl.startsWith('http')
    ? baseUrl
    : `${window.location.origin}${baseUrl}`;
  const googleRedirectUri = `${apiBaseUrl}/org/${organization}/auth/google/callback`;
  const googleAuthorizedOrigin = window.location.origin;
  const microsoftAllowedDomainsValue = (
    org?.microsoftOidc?.allowedDomains || []
  ).join(', ');
  const hasMicrosoftClientId = !!org?.microsoftOidc?.clientId?.trim();
  const hasMicrosoftClientSecret = !!org?.microsoftOidc?.clientSecretConfigured;
  const microsoftRedirectUri = `${apiBaseUrl}/org/${organization}/auth/microsoft/callback`;

  const copyValue = async (field: string, value: string) => {
    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(value);
      } else {
        const textArea = document.createElement('textarea');
        textArea.value = value;
        textArea.style.position = 'fixed';
        textArea.style.opacity = '0';
        document.body.appendChild(textArea);
        textArea.select();
        document.execCommand('copy');
        document.body.removeChild(textArea);
      }

      setCopiedField(field);
      if (copyResetTimeout.current) {
        window.clearTimeout(copyResetTimeout.current);
      }
      copyResetTimeout.current = window.setTimeout(
        () => setCopiedField(null),
        2000
      );
    } catch {
      setCopiedField(null);
    }
  };

  return (
    <>
      <Typography variant="body2">
        {t('loginConfigurationDescription')}
      </Typography>
      <Line />
      <LoginConfigurationPanel>
        <LoginProviderOptions>
          <LoginProviderOption
            $selected={isFlexibleSignin}
            onClick={() => onSelectAuthProvider('cognito')}
          >
            <LoginProviderOptionBody>
              <LoginProviderOptionHeader>
                <Typography variant="subtitle1">
                  {t('loginOptionCognitoTitle')}
                </Typography>
                {isFlexibleSignin && (
                  <LoginProviderBadge variant="caption">
                    {t('loginProviderActive')}
                  </LoginProviderBadge>
                )}
              </LoginProviderOptionHeader>
              <Typography variant="body2">
                {t('loginOptionCognitoDescription')}
              </Typography>
            </LoginProviderOptionBody>
          </LoginProviderOption>
          <LoginProviderOption
            $selected={activeAuthProvider === 'google'}
            onClick={() => onSelectAuthProvider('google')}
          >
            <LoginProviderOptionBody>
              <LoginProviderOptionHeader>
                <Typography variant="subtitle1">
                  {t('loginOptionGoogleTitle')}
                </Typography>
                {activeAuthProvider === 'google' && (
                  <LoginProviderBadge variant="caption">
                    {t('loginProviderActive')}
                  </LoginProviderBadge>
                )}
              </LoginProviderOptionHeader>
              <Typography variant="body2">
                {t('loginOptionGoogleDescription')}
              </Typography>
            </LoginProviderOptionBody>
          </LoginProviderOption>
          <LoginProviderOption
            $selected={activeAuthProvider === 'microsoft'}
            onClick={() => onSelectAuthProvider('microsoft')}
          >
            <LoginProviderOptionBody>
              <LoginProviderOptionHeader>
                <Typography variant="subtitle1">
                  {t('loginOptionMicrosoftTitle')}
                </Typography>
                {activeAuthProvider === 'microsoft' && (
                  <LoginProviderBadge variant="caption">
                    {t('loginProviderActive')}
                  </LoginProviderBadge>
                )}
              </LoginProviderOptionHeader>
              <Typography variant="body2">
                {t('loginOptionMicrosoftDescription')}
              </Typography>
            </LoginProviderOptionBody>
          </LoginProviderOption>
        </LoginProviderOptions>

        <LoginProviderDetails>
          {activeAuthProvider === 'google' ? (
            <>
              <LoginSectionCard>
                <LoginSectionHeading variant="subtitle1">
                  {t('googleWorkspaceSetup')}
                </LoginSectionHeading>
                <LoginFieldHint variant="body2">
                  {t('googleWorkspaceSetupDescription')}
                </LoginFieldHint>
                <LoginStatusGrid>
                  <LoginStatusCard>
                    <LoginStatusHeader>
                      <LoginStatusIcon>
                        <LockIcon fontSize="small" />
                      </LoginStatusIcon>
                      <LoginStatusBadge
                        variant="caption"
                        $tone={
                          hasGoogleClientId && hasGoogleClientSecret
                            ? 'positive'
                            : 'warning'
                        }
                      >
                        {hasGoogleClientId && hasGoogleClientSecret
                          ? t('loginStatusConfigured')
                          : t('loginStatusNeedsAction')}
                      </LoginStatusBadge>
                    </LoginStatusHeader>
                    <Typography variant="subtitle2">
                      {t('googleStatusCredentialsTitle')}
                    </Typography>
                    <LoginFieldHint variant="body2">
                      {hasGoogleClientId && hasGoogleClientSecret
                        ? t('googleStatusCredentialsConfigured')
                        : t('googleStatusCredentialsIncomplete')}
                    </LoginFieldHint>
                  </LoginStatusCard>
                  <LoginStatusCard>
                    <LoginStatusHeader>
                      <LoginStatusIcon>
                        <LinkIcon fontSize="small" />
                      </LoginStatusIcon>
                      <LoginStatusBadge variant="caption" $tone="neutral">
                        {t('loginStatusReady')}
                      </LoginStatusBadge>
                    </LoginStatusHeader>
                    <Typography variant="subtitle2">
                      {t('googleStatusRedirectTitle')}
                    </Typography>
                    <LoginFieldHint variant="body2">
                      {t('googleStatusRedirectDescription')}
                    </LoginFieldHint>
                  </LoginStatusCard>
                  <LoginStatusCard>
                    <LoginStatusHeader>
                      <LoginStatusIcon>
                        <PeopleIcon fontSize="small" />
                      </LoginStatusIcon>
                      <LoginStatusBadge
                        variant="caption"
                        $tone={
                          hasRestrictedGoogleDomains ? 'positive' : 'neutral'
                        }
                      >
                        {hasRestrictedGoogleDomains
                          ? t('loginStatusRestricted')
                          : t('loginStatusOpen')}
                      </LoginStatusBadge>
                    </LoginStatusHeader>
                    <Typography variant="subtitle2">
                      {t('googleStatusAccessTitle')}
                    </Typography>
                    <LoginFieldHint variant="body2">
                      {hasRestrictedGoogleDomains
                        ? t('googleStatusAccessRestricted')
                        : t('googleStatusAccessOpen')}
                    </LoginFieldHint>
                  </LoginStatusCard>
                </LoginStatusGrid>
                <Typography variant="subtitle2">
                  {t('googleSetupChecklistTitle')}
                </Typography>
                <LoginStepGrid>
                  <LoginStepCard>
                    <LoginStepNumber>1</LoginStepNumber>
                    <Typography variant="body2">
                      {t('googleSetupStepProject')}
                    </Typography>
                  </LoginStepCard>
                  <LoginStepCard>
                    <LoginStepNumber>2</LoginStepNumber>
                    <Typography variant="body2">
                      {t('googleSetupStepConsent')}
                    </Typography>
                  </LoginStepCard>
                  <LoginStepCard>
                    <LoginStepNumber>3</LoginStepNumber>
                    <Typography variant="body2">
                      {t('googleSetupStepClient')}
                    </Typography>
                  </LoginStepCard>
                  <LoginStepCard>
                    <LoginStepNumber>4</LoginStepNumber>
                    <Typography variant="body2">
                      {t('googleSetupStepCredentials')}
                    </Typography>
                  </LoginStepCard>
                </LoginStepGrid>
                <LoginSettingsGrid>
                  <LoginField>
                    <LoginFieldHeader>
                      <LoginFieldLabel variant="caption">
                        {t('googleRedirectUriLabel')}
                      </LoginFieldLabel>
                      <CopyValueButton
                        size="small"
                        color="primary"
                        startIcon={
                          copiedField === 'redirectUri' ? (
                            <CheckCircleIcon fontSize="small" />
                          ) : (
                            <CopyIcon fontSize="small" />
                          )
                        }
                        onClick={() =>
                          copyValue('redirectUri', googleRedirectUri)
                        }
                      >
                        {copiedField === 'redirectUri'
                          ? t('copied')
                          : t('copyValue')}
                      </CopyValueButton>
                    </LoginFieldHeader>
                    <LoginTextInput initialValue={googleRedirectUri} readOnly />
                  </LoginField>
                  <LoginField>
                    <LoginFieldHeader>
                      <LoginFieldLabel variant="caption">
                        {t('googleAuthorizedOriginLabel')}
                      </LoginFieldLabel>
                      <CopyValueButton
                        size="small"
                        color="primary"
                        startIcon={
                          copiedField === 'authorizedOrigin' ? (
                            <CheckCircleIcon fontSize="small" />
                          ) : (
                            <CopyIcon fontSize="small" />
                          )
                        }
                        onClick={() =>
                          copyValue('authorizedOrigin', googleAuthorizedOrigin)
                        }
                      >
                        {copiedField === 'authorizedOrigin'
                          ? t('copied')
                          : t('copyValue')}
                      </CopyValueButton>
                    </LoginFieldHeader>
                    <LoginTextInput
                      initialValue={googleAuthorizedOrigin}
                      readOnly
                    />
                  </LoginField>
                </LoginSettingsGrid>
              </LoginSectionCard>
              <LoginSectionCard>
                <LoginSectionHeading variant="subtitle2">
                  {t('googleCredentialsSectionTitle')}
                </LoginSectionHeading>
                <LoginGuideValue>
                  <LoginFieldHint variant="body2">
                    {t('googleSetupDomainHint')}
                  </LoginFieldHint>
                </LoginGuideValue>
                <LoginSettingsGrid>
                  <LoginField>
                    <LoginFieldLabel variant="caption">
                      {t('googleClientId')}
                    </LoginFieldLabel>
                    <LoginTextInput
                      initialValue={org?.googleOidc?.clientId}
                      onChange={(value: string) =>
                        onUpdateGoogleOidc({ clientId: value })
                      }
                    />
                  </LoginField>
                  <LoginField>
                    <LoginFieldLabel variant="caption">
                      {t('googleClientSecret')}
                    </LoginFieldLabel>
                    <LoginTextInput
                      type="password"
                      initialValue=""
                      placeholder={
                        org?.googleOidc?.clientSecretConfigured
                          ? t('configuredSecretPlaceholder')
                          : ''
                      }
                      onChange={(value: string) =>
                        onUpdateGoogleOidc({ clientSecret: value })
                      }
                    />
                  </LoginField>
                </LoginSettingsGrid>
                <LoginField>
                  <LoginFieldLabel variant="caption">
                    {t('googleAllowedDomains')}
                  </LoginFieldLabel>
                  <LoginFieldHint variant="body2">
                    {t('googleAllowedDomainsHint')}
                  </LoginFieldHint>
                  <LoginTextInput
                    initialValue={googleAllowedDomainsValue}
                    placeholder="example.com, partner.org"
                    onChange={(value: string) =>
                      onUpdateGoogleOidc({
                        allowedDomains: value
                          .split(',')
                          .map(item => item.trim())
                          .filter(Boolean)
                      })
                    }
                  />
                </LoginField>
                <LoginToggleRow>
                  <Box>
                    <Typography variant="subtitle2">
                      {t('googleAutoCreateUsers')}
                    </Typography>
                    <Typography variant="body2">
                      {t('googleAutoCreateUsersDescription')}
                    </Typography>
                  </Box>
                  <Switch
                    checked={!!org?.googleOidc?.autoCreateUsers}
                    onChange={event =>
                      onUpdateGoogleOidc({
                        autoCreateUsers: event.target.checked
                      })
                    }
                  />
                </LoginToggleRow>
              </LoginSectionCard>
            </>
          ) : activeAuthProvider === 'microsoft' ? (
            <>
              <LoginSectionCard>
                <LoginSectionHeading variant="subtitle1">
                  {t('microsoftWorkspaceSetup')}
                </LoginSectionHeading>
                <LoginFieldHint variant="body2">
                  {t('microsoftWorkspaceSetupDescription')}
                </LoginFieldHint>
                <LoginGuideValue>
                  <LoginFieldHint variant="body2">
                    {hasMicrosoftClientId && hasMicrosoftClientSecret
                      ? t('microsoftCredentialsConfigured')
                      : t('microsoftCredentialsIncomplete')}
                  </LoginFieldHint>
                </LoginGuideValue>
                <LoginSettingsGrid>
                  <LoginField>
                    <LoginFieldHeader>
                      <LoginFieldLabel variant="caption">
                        {t('microsoftRedirectUriLabel')}
                      </LoginFieldLabel>
                      <CopyValueButton
                        size="small"
                        color="primary"
                        startIcon={
                          copiedField === 'microsoftRedirectUri' ? (
                            <CheckCircleIcon fontSize="small" />
                          ) : (
                            <CopyIcon fontSize="small" />
                          )
                        }
                        onClick={() =>
                          copyValue('microsoftRedirectUri', microsoftRedirectUri)
                        }
                      >
                        {copiedField === 'microsoftRedirectUri'
                          ? t('copied')
                          : t('copyValue')}
                      </CopyValueButton>
                    </LoginFieldHeader>
                    <LoginTextInput initialValue={microsoftRedirectUri} readOnly />
                  </LoginField>
                  <LoginField>
                    <LoginFieldLabel variant="caption">
                      {t('microsoftTenantId')}
                    </LoginFieldLabel>
                    <LoginFieldHint variant="body2">
                      {t('microsoftTenantIdHint')}
                    </LoginFieldHint>
                    <LoginTextInput
                      initialValue={org?.microsoftOidc?.tenantId}
                      onChange={(value: string) =>
                        onUpdateMicrosoftOidc({ tenantId: value })
                      }
                    />
                  </LoginField>
                </LoginSettingsGrid>
              </LoginSectionCard>
              <LoginSectionCard>
                <LoginSectionHeading variant="subtitle2">
                  {t('microsoftCredentialsSectionTitle')}
                </LoginSectionHeading>
                <LoginGuideValue>
                  <LoginFieldHint variant="body2">
                    {t('microsoftSetupHint')}
                  </LoginFieldHint>
                </LoginGuideValue>
                <LoginSettingsGrid>
                  <LoginField>
                    <LoginFieldLabel variant="caption">
                      {t('microsoftClientId')}
                    </LoginFieldLabel>
                    <LoginTextInput
                      initialValue={org?.microsoftOidc?.clientId}
                      onChange={(value: string) =>
                        onUpdateMicrosoftOidc({ clientId: value })
                      }
                    />
                  </LoginField>
                  <LoginField>
                    <LoginFieldLabel variant="caption">
                      {t('microsoftClientSecret')}
                    </LoginFieldLabel>
                    <LoginTextInput
                      type="password"
                      initialValue=""
                      placeholder={
                        org?.microsoftOidc?.clientSecretConfigured
                          ? t('configuredSecretPlaceholder')
                          : ''
                      }
                      onChange={(value: string) =>
                        onUpdateMicrosoftOidc({ clientSecret: value })
                      }
                    />
                  </LoginField>
                </LoginSettingsGrid>
                <LoginField>
                  <LoginFieldLabel variant="caption">
                    {t('microsoftAllowedDomains')}
                  </LoginFieldLabel>
                  <LoginFieldHint variant="body2">
                    {t('microsoftAllowedDomainsHint')}
                  </LoginFieldHint>
                  <LoginTextInput
                    initialValue={microsoftAllowedDomainsValue}
                    placeholder="example.com, partner.org"
                    onChange={(value: string) =>
                      onUpdateMicrosoftOidc({
                        allowedDomains: value
                          .split(',')
                          .map(item => item.trim())
                          .filter(Boolean)
                      })
                    }
                  />
                </LoginField>
                <LoginToggleRow>
                  <Box>
                    <Typography variant="subtitle2">
                      {t('microsoftAutoCreateUsers')}
                    </Typography>
                    <Typography variant="body2">
                      {t('microsoftAutoCreateUsersDescription')}
                    </Typography>
                  </Box>
                  <Switch
                    checked={!!org?.microsoftOidc?.autoCreateUsers}
                    onChange={event =>
                      onUpdateMicrosoftOidc({
                        autoCreateUsers: event.target.checked
                      })
                    }
                  />
                </LoginToggleRow>
              </LoginSectionCard>
            </>
          ) : (
            <>
              <Typography variant="subtitle1">
                {t('loginOptionCognitoTitle')}
              </Typography>
              <Typography variant="body2">
                {t('loginOptionCognitoDetails')}
              </Typography>
            </>
          )}
        </LoginProviderDetails>
      </LoginConfigurationPanel>
    </>
  );
}
